import { useEffect, useMemo, useState } from "react";
import {
  ChevronDown,
  ChevronRight,
  Crown,
  Info,
  Loader2,
  Mail,
  Medal,
  Minus,
  RefreshCw,
  ShieldCheck,
  Sparkles,
  TrendingDown,
  TrendingUp,
  Trophy,
} from "lucide-react";
import { toast } from "sonner";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { ExportButton } from "@/components/ExportButton";
import { SortBar } from "@/components/SortHeader";
import { sortRows, useSort } from "@/lib/sorting";
import { useAuth } from "@/context/AuthContext";
import { api, fmtWhen } from "@/lib/api";

const MEDAL = ["hsl(43 96% 56%)", "hsl(220 9% 72%)", "hsl(28 62% 48%)"];

const Bar = ({ value, max }) => (
  <div className="h-1.5 w-full bg-muted">
    <div
      className="h-1.5 bg-primary transition-[width] duration-700"
      style={{ width: `${max ? Math.min(100, (value / max) * 100) : 0}%` }}
    />
  </div>
);

const Movement = ({ value }) => {
  if (!value)
    return (
      <span className="flex items-center gap-1 text-muted-foreground">
        <Minus className="h-3 w-3" /> holding
      </span>
    );
  const up = value > 0;
  return (
    <span
      className="flex items-center gap-1"
      style={{ color: up ? "hsl(var(--healthy))" : "hsl(var(--at-risk))" }}
    >
      {up ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
      {up ? `up ${value}` : `down ${Math.abs(value)}`}
    </span>
  );
};

const Podium = ({ rows }) => {
  const top = rows.slice(0, 3);
  const order = [1, 0, 2].filter((i) => top[i]);
  const height = ["h-48", "h-40", "h-32"];
  return (
    <div className="mt-6 grid grid-cols-3 items-end gap-px bg-border" data-testid="lb-podium">
      {order.map((i) => {
        const row = top[i];
        return (
          <div
            key={row.unit}
            className={`relative flex flex-col justify-end bg-card p-4 ${height[i]}`}
            data-testid={`lb-podium-${i + 1}`}
            style={{ borderTop: `3px solid ${MEDAL[i]}` }}
          >
            <div className="absolute right-3 top-3">
              {i === 0 ? (
                <Crown className="h-5 w-5" style={{ color: MEDAL[0] }} />
              ) : (
                <Medal className="h-4 w-4" style={{ color: MEDAL[i] }} />
              )}
            </div>
            <div className="font-data text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
              Rank {row.rank}
            </div>
            <div className="font-display text-2xl font-black tracking-tight">{row.unit}</div>
            <div className="truncate text-[11px] text-muted-foreground">{row.unit_name}</div>
            <div className="font-data mt-2 text-3xl font-light" style={{ color: MEDAL[i] }}>
              {row.score}
            </div>
          </div>
        );
      })}
    </div>
  );
};

const Spark = ({ history = [], from, weeks = 0 }) => {
  const points = history.filter((h) => h.score !== null && h.score !== undefined);
  if (points.length < 2)
    return (
      <p className="text-[11px] text-muted-foreground">
        The portal has been keeping this score since {from || "this week"} — the twelve-week line
        fills in from here.
      </p>
    );
  const w = 260;
  const h = 44;
  const scores = points.map((p) => p.score);
  const low = Math.min(...scores);
  const best = Math.max(...scores);
  const span = best - low || 1;
  const step = history.length > 1 ? w / (history.length - 1) : w;
  const y = (score) => h - 4 - ((score - low) / span) * (h - 10);
  const plot = history
    .map((p, i) =>
      p.score === null || p.score === undefined ? null : [i * step, y(p.score)],
    )
    .filter(Boolean);
  const line = plot.map(([px, py]) => `${px.toFixed(1)},${py.toFixed(1)}`).join(" ");
  const area = `${plot[0][0].toFixed(1)},${h} ${line} ${plot[plot.length - 1][0].toFixed(1)},${h}`;
  const last = plot[plot.length - 1];
  return (
    <div>
      <svg viewBox={`0 0 ${w} ${h}`} className="h-14 w-full" preserveAspectRatio="none">
        <polygon points={area} style={{ fill: "hsl(var(--primary))", opacity: 0.14 }} />
        <polyline
          points={line}
          fill="none"
          strokeWidth="2"
          strokeLinejoin="round"
          style={{ stroke: "hsl(var(--primary))" }}
          vectorEffect="non-scaling-stroke"
        />
        {plot.map(([px, py], i) => (
          <circle
            key={i}
            cx={px}
            cy={py}
            r={i === plot.length - 1 ? 3.5 : 1.8}
            style={{ fill: "hsl(var(--primary))" }}
            vectorEffect="non-scaling-stroke"
          />
        ))}
        <circle cx={last[0]} cy={last[1]} r="3.5" style={{ fill: "hsl(var(--primary))" }} />
      </svg>
      <div className="font-data mt-1 flex flex-wrap justify-between gap-x-4 text-[10px] uppercase tracking-widest text-muted-foreground">
        <span>week of {points[0].week}</span>
        <span>
          best {best} · lowest {low} · now {points[points.length - 1].score}
        </span>
        <span>{weeks} week(s) recorded</span>
      </div>
    </div>
  );
};

const Row = ({ row, total }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="bg-card" data-testid={`lb-row-${row.unit}`}>
      <button
        type="button"
        data-testid={`lb-expand-${row.unit}`}
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-4 px-4 py-4 text-left transition-colors duration-200 hover:bg-muted"
      >
        <span
          className="font-data w-10 shrink-0 text-center text-xl font-bold"
          style={row.rank <= 3 ? { color: MEDAL[row.rank - 1] } : undefined}
        >
          {row.rank}
        </span>
        <span className="min-w-0 flex-1">
          <span className="flex flex-wrap items-center gap-2">
            <span className="font-display text-base font-bold">{row.unit}</span>
            <span className="truncate text-[11px] text-muted-foreground">{row.unit_name}</span>
          </span>
          <span className="mt-2 block">
            <Bar value={row.score} max={total} />
          </span>
          <span className="font-data mt-1.5 flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-muted-foreground">
            <span>{row.use_cases} usecases</span>
            <span>₹{row.expected_cr} Cr expected</span>
            <span>₹{row.vetted_cr} Cr vetted</span>
            <span>{row.on_track_pct}% on track</span>
            <span>{row.training_pct}% training</span>
            <span>{row.team} in the CoDE team</span>
            <span>
              {row.bonus_points > 0 ? `+${row.bonus_points}` : "no"} safety bonus
            </span>
          </span>
        </span>
        <span className="shrink-0 text-right">
          <span
            className="font-data block text-2xl font-bold"
            data-testid={`lb-score-${row.unit}`}
          >
            {row.score}
          </span>
          <span className="font-data block text-[10px] uppercase tracking-widest text-muted-foreground">
            of {total}
          </span>
          <span className="font-data mt-1 flex items-center justify-end gap-1 text-[10px] uppercase tracking-widest">
            <Movement value={row.movement} />
          </span>
        </span>
        {open ? (
          <ChevronDown className="h-4 w-4 shrink-0" />
        ) : (
          <ChevronRight className="h-4 w-4 shrink-0" />
        )}
      </button>

      {open && (
        <>
          <div className="border-t border-border bg-card p-4" data-testid={`lb-spark-${row.unit}`}>
            <div className="label-caps">Score over the last twelve weeks</div>
            <div className="mt-3">
              <Spark history={row.history} from={row.history_from} weeks={row.history_weeks} />
            </div>
          </div>
          <div className="grid gap-px border-t border-border bg-border sm:grid-cols-2 lg:grid-cols-4">
          {[...row.measures, row.bonus].filter(Boolean).map((m) => (
            <div
              key={m.key}
              className="bg-card p-4"
              data-testid={`lb-measure-${row.unit}-${m.key}`}
              title={m.how}
            >
              <div className="flex items-baseline justify-between gap-2">
                <span className="font-display text-xs font-bold uppercase tracking-wider">
                  {m.label}
                </span>
                <span className="font-data shrink-0 text-sm font-bold">
                  {m.points}
                  <span className="text-muted-foreground">/{m.max}</span>
                </span>
              </div>
              <div className="mt-2">
                <Bar value={m.points} max={m.max} />
              </div>
              <div className="font-data mt-2 text-[11px]">{m.value}</div>
              <div className="text-[11px] text-muted-foreground">{m.detail}</div>
              {m.relative && m.topper && (
                <div
                  className="mt-2 border-t border-border pt-2 text-[11px]"
                  data-testid={`lb-topper-${row.unit}-${m.key}`}
                >
                  <span className="label-caps flex items-center gap-1 text-primary">
                    <Crown className="h-3 w-3" /> Proportioned against the best
                  </span>
                  <span className="mt-1 block text-muted-foreground">{m.topper.working}</span>
                </div>
              )}
              {m.key === "safety_bonus" && m.earned !== m.points && (
                <div className="mt-2 border-t border-border pt-2 text-[11px] text-muted-foreground">
                  {m.earned} bonus point(s) earned; {m.points} added, so the final score stays at{" "}
                  {total}.
                </div>
              )}
            </div>
          ))}
          </div>
        </>
      )}
    </div>
  );
};

export default function Leaderboard() {
  const { isAdmin, shows } = useAuth();
  const [data, setData] = useState(null);
  const [board, setBoard] = useState("plants");
  const [busy, setBusy] = useState(false);
  const [help, setHelp] = useState(false);
  const [askSend, setAskSend] = useState(false);
  const [sending, setSending] = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      const res = await api.get("/leaderboard");
      setData(res.data);
    } finally {
      setBusy(false);
    }
  };

  const sendCards = async () => {
    setAskSend(false);
    setSending(true);
    try {
      const { data: out } = await api.post("/leaderboard/rank-cards/send?dry_run=false");
      toast.success("This week's rank cards have gone out", { description: out.summary });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "The rank cards could not be sent.");
    } finally {
      setSending(false);
    }
  };

  useEffect(() => {
    load();
    const timer = setInterval(load, 30000);
    return () => clearInterval(timer);
  }, []);

  const { sort, on: sortBy } = useSort("leaderboard");

  const current = useMemo(
    () => (data?.boards || []).find((b) => b.key === board) || data?.boards?.[0],
    [data, board],
  );

  if (!data)
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );

  if (data.coming_soon)
    return (
      <div className="p-5 sm:p-8" data-testid="leaderboard-coming-soon">
        <div className="relative overflow-hidden border border-border bg-card p-10 sm:p-16">
          <div
            className="pointer-events-none absolute -right-16 -top-16 h-64 w-64 rounded-full opacity-20 blur-3xl"
            style={{ background: "hsl(var(--primary))" }}
          />
          <div className="label-caps flex items-center gap-2 text-primary">
            <Trophy className="h-3.5 w-3.5" /> Live leaderboard
          </div>
          <h1 className="mt-3 font-display text-4xl font-black tracking-tight sm:text-5xl">
            Coming soon
          </h1>
          <p className="mt-4 max-w-xl text-sm text-muted-foreground">{data.message}</p>
          <div className="font-data mt-8 flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
            <Sparkles className="h-3.5 w-3.5" /> Plants · Central Units · Mines · Overall SAIL
          </div>
        </div>
      </div>
    );

  return (
    <div className="p-5 sm:p-8" data-testid="leaderboard-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="max-w-3xl">
          <div className="label-caps flex items-center gap-2 text-primary">
            <Trophy className="h-3.5 w-3.5" /> Live leaderboard
          </div>
          <h1 className="mt-2 font-display text-3xl font-black tracking-tight sm:text-4xl">
            Who is driving Pravartanam hardest
          </h1>
          <p className="mt-2 text-sm text-muted-foreground">
            The <b>{data.title}</b> — {data.measures?.length} measures carrying{" "}
            {data.total_points} points, and a Safety Usecase bonus of {data.bonus?.points} which can
            only carry a unit up to {data.total_points}. Every figure is read from the portal's own
            register, training tracker, team list and sign-in trail. Three measures are proportioned
            against the best unit on the same board — open your unit's row and it names the unit that
            topped each of them, and with what figure. The full scoring is set out at the foot of
            this page, word for word as SDTD issued it.
          </p>
        </div>
        <div className="text-right">
          <div className="flex flex-wrap items-center justify-end gap-px">
            {isAdmin && shows("leaderboard.cards") && (
              <button
                type="button"
                data-testid="lb-send-cards"
                onClick={() => setAskSend(true)}
                disabled={sending}
                className="flex h-10 items-center gap-2 border border-border px-3 font-display text-[10px] font-bold uppercase tracking-widest transition-colors duration-200 hover:bg-muted disabled:opacity-50"
              >
                <Mail className={`h-3.5 w-3.5 ${sending ? "animate-pulse" : ""}`} /> Send rank cards
              </button>
            )}
            <button
              type="button"
              data-testid="lb-refresh"
              onClick={load}
              className="flex h-10 items-center gap-2 border border-border px-3 font-display text-[10px] font-bold uppercase tracking-widest transition-colors duration-200 hover:bg-muted"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${busy ? "animate-spin" : ""}`} /> Recalculate
            </button>
            <ExportButton
              path="/leaderboard/export"
              params={{ board_key: board }}
              testId="export-leaderboard"
              label="Download this board"
              className="h-10 !px-3"
            />
          </div>
          <div className="font-data mt-2 text-[10px] uppercase tracking-widest text-muted-foreground">
            worked out {fmtWhen(data.computed_at)}
          </div>
        </div>
      </div>

      {(data.recent_movements || []).length > 0 && (
        <div className="mt-6 border border-border bg-card p-4" data-testid="lb-movements">
          <div className="label-caps">Movements — the last seven days</div>
          <div className="mt-3 flex flex-wrap gap-2">
            {(data.recent_movements || []).slice(0, 8).map((m, i) => (
              <span
                key={`${m.unit}-${m.at}-${i}`}
                data-testid={`lb-movement-${m.unit}`}
                className="flex items-center gap-2 border border-border px-3 py-2 text-[11px]"
              >
                {m.direction === "down" ? (
                  <TrendingDown className="h-3.5 w-3.5" style={{ color: "hsl(var(--at-risk))" }} />
                ) : (
                  <TrendingUp className="h-3.5 w-3.5" style={{ color: "hsl(var(--healthy))" }} />
                )}
                <b>{m.unit}</b>
                <span className="text-muted-foreground">
                  {m.from_rank} → {m.to_rank} on {m.board_label}                  {m.other_unit ? ` · ${m.other_unit}` : ""}
                  {m.measure ? ` · ${m.measure}` : ""}
                </span>
              </span>
            ))}
          </div>
        </div>
      )}

      {shows("leaderboard.boards") && (
      <div className="mt-8 flex flex-wrap gap-px bg-border" data-testid="lb-boards">
        {(data.boards || []).map((b) => (
          <button
            key={b.key}
            type="button"
            data-testid={`board-tab-${b.key}`}
            onClick={() => setBoard(b.key)}
            className={`flex-1 px-4 py-3 text-left font-display text-xs font-bold uppercase tracking-widest transition-colors duration-200 ${
              (current?.key || "") === b.key
                ? "bg-primary text-primary-foreground"
                : "bg-card text-muted-foreground hover:bg-muted hover:text-foreground"
            }`}
          >
            {b.label}
            <span className="mt-0.5 block font-data text-[10px] tracking-normal opacity-80">
              {b.rows.length} unit(s)
            </span>
          </button>
        ))}
      </div>
      )}

      {current && (
        <>
          <Podium rows={current.rows} />

          <div className="mt-px border border-border bg-card px-5 py-3">
            <SortBar
              testId="leaderboard-sort"
              sort={sort}
              onSort={sortBy}
              columns={[
                ["rank", "Rank"],
                ["unit", "Unit"],
                ["score", "Score"],
                ["movement", "Movement"],
                ["use_cases", "Use cases"],
                ["expected_cr", "Benefit"],
              ]}
            />
          </div>

          <div className="mt-px divide-y divide-border border border-border" data-testid="lb-table">
            {sortRows(current.rows, sort.key, sort.dir).map((row) => (
              <Row key={row.unit} row={row} total={data.total_points} />
            ))}
            {!current.rows.length && (
              <p className="bg-card p-8 text-center text-sm text-muted-foreground">
                No unit on this board carries data yet.
              </p>
            )}
          </div>

          <div className="mt-6 grid gap-px bg-border sm:grid-cols-2">
            <div className="bg-card p-5" data-testid="lb-most-improved">
              <div className="label-caps">Most improved</div>
              {current.most_improved ? (
                <p className="mt-2 text-sm">
                  <b>
                    {current.most_improved.unit} — {current.most_improved.unit_name}
                  </b>{" "}
                  is up{" "}
                  <span className="font-data font-bold" style={{ color: "hsl(var(--healthy))" }}>
                    {current.most_improved.gain}
                  </span>{" "}
                  points since {current.most_improved.since}.
                </p>
              ) : (
                <p className="mt-2 text-sm text-muted-foreground">
                  The portal needs a month of scores before it can say who has improved most. Ranks
                  are being recorded from today.
                </p>
              )}
            </div>
            <div className="bg-card p-5" data-testid="lb-tie-breaker">
              <div className="label-caps flex items-center gap-2">
                <ShieldCheck className="h-3.5 w-3.5" /> {data.bonus?.label} — the tie-breaker
              </div>
              <p className="mt-2 whitespace-pre-line text-sm text-muted-foreground">
                {data.bonus?.how}
              </p>
              <div className="font-data mt-3 flex flex-wrap gap-x-4 gap-y-1 text-[11px]">
                {current.rows.map((r) => (
                  <span key={r.unit}>
                    {r.unit} <b>{r.bonus_earned}</b>
                    <span className="text-muted-foreground">
                      /{data.bonus?.points}
                      {r.bonus_points !== r.bonus_earned ? ` · ${r.bonus_points} added` : ""}
                    </span>
                  </span>
                ))}
              </div>
            </div>
          </div>

          {shows("leaderboard.measures") && (
          <div className="mt-6 border border-border bg-card" data-testid="lb-how-it-works">
            <div className="border-b border-border p-5">
              <div className="label-caps flex items-center gap-2">
                <Info className="h-3.5 w-3.5" /> How the score is worked out
              </div>
              <h2 className="mt-2 font-display text-base font-bold md:text-lg">{data.title}</h2>
              <p className="mt-1 text-xs text-muted-foreground">{data.subtitle}</p>
              <p className="mt-1 text-[11px] text-muted-foreground">
                Word for word from SDTD's scoring sheet. Nothing here is entered by hand — every
                figure is read from the portal itself.
              </p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/60">
                  <tr className="text-left font-data text-[10px] uppercase tracking-widest text-muted-foreground">
                    <th className="px-5 py-3">{data.headers?.[0] || "Measure"}</th>
                    <th className="px-5 py-3 text-right">{data.headers?.[1] || "Max Points"}</th>
                    <th className="px-5 py-3">{data.headers?.[2] || "How it is worked out"}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {[...(data.measures || []), data.bonus].filter(Boolean).map((m) => (
                    <tr key={m.key} data-testid={`lb-rule-${m.key}`} className="align-top">
                      <td className="px-5 py-3 font-display text-xs font-bold uppercase tracking-wider">
                        {m.label}
                      </td>
                      <td className="font-data px-5 py-3 text-right text-base font-bold">
                        {m.points}
                      </td>
                      <td className="whitespace-pre-line px-5 py-3 text-xs leading-relaxed text-muted-foreground">
                        {m.how}
                      </td>
                    </tr>
                  ))}
                  <tr className="bg-muted/40">
                    <td className="px-5 py-3 font-display text-xs font-bold uppercase tracking-wider">
                      Total
                    </td>
                    <td className="font-data px-5 py-3 text-right text-base font-black">
                      {data.total_points}
                    </td>
                    <td className="px-5 py-3 text-xs text-muted-foreground" />
                  </tr>
                </tbody>
              </table>
            </div>
            <ul className="space-y-2 border-t border-border p-5 text-xs leading-relaxed text-muted-foreground">
              {(data.notes || []).map((note, i) => (
                <li key={i} className="flex gap-2">
                  <span className="text-primary">·</span>
                  {note}
                </li>
              ))}
            </ul>
          </div>
          )}
        </>
      )}

      <ConfirmDialog
        open={askSend}
        onOpenChange={setAskSend}
        testId="send-cards-confirm"
        title="Send this week's rank cards?"
        description="Every unit's SPOC receives their own card — rank, score out of 100 and every measure — and SDTD receives one copy carrying all four boards. While the Leaderboard is being readied only SDTD's copy goes out. It is sent immediately."
        confirmLabel="Yes, send them"
        onConfirm={sendCards}
      />
    </div>
  );
}
